import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, Wind } from "lucide-react";

// Phases: 0 inhale, 1 hold (after inhale), 2 exhale, 3 hold (after exhale)
const PHASE_LABELS = ["Inhale", "Hold", "Exhale", "Hold"];

const BreathworkTimer = ({ pattern, cycles, name, instruction }) => {
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(pattern[0] || 0);
  const [cycle, setCycle] = useState(1);
  const [done, setDone] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s > 1) return s - 1;
        // Move to next phase
        let nextPhase = phase + 1;
        let nextCycle = cycle;
        // Skip phases with 0 seconds
        while (nextPhase < 4 && (pattern[nextPhase] || 0) === 0) {
          nextPhase += 1;
        }
        if (nextPhase >= 4) {
          nextPhase = 0;
          nextCycle += 1;
          // Skip phase 0 if 0 (unlikely)
          while (nextPhase < 4 && (pattern[nextPhase] || 0) === 0) {
            nextPhase += 1;
          }
        }
        if (nextCycle > cycles) {
          clearInterval(intervalRef.current);
          setRunning(false);
          setDone(true);
          return 0;
        }
        setPhase(nextPhase);
        setCycle(nextCycle);
        return pattern[nextPhase] || 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running, phase, cycle, cycles, pattern]);

  const reset = () => {
    clearInterval(intervalRef.current);
    setRunning(false);
    setPhase(0);
    setCycle(1);
    setSecondsLeft(pattern[0] || 0);
    setDone(false);
  };

  const start = () => {
    if (done) reset();
    setRunning((r) => !r);
  };

  // Visual scale based on phase
  const scaleClass = (() => {
    if (!running && !done) return "scale-100";
    if (phase === 0) return "scale-150";
    if (phase === 1) return "scale-150";
    if (phase === 2) return "scale-100";
    return "scale-100";
  })();

  return (
    <div className="rounded-3xl bg-white border border-[#E2EBE6] p-6 shadow-[0_8px_32px_rgba(35,75,56,0.06)]" data-testid="breathwork-card">
      <div className="flex items-center gap-2 text-[#234B38]">
        <Wind className="w-4 h-4" strokeWidth={1.75} />
        <span className="text-xs font-semibold uppercase tracking-[0.2em]">Breathwork</span>
      </div>
      <h3 className="mt-2 text-xl font-medium text-[#1A2E24]" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="breathwork-name">
        {name}
      </h3>
      <p className="text-sm text-[#5C7568] mt-1">{instruction}</p>

      <div className="mt-6 flex flex-col items-center justify-center py-4">
        <div className="relative w-40 h-40 flex items-center justify-center">
          <div
            className={`absolute inset-0 rounded-full bg-[#234B38]/10 transition-transform ease-in-out ${scaleClass}`}
            style={{ transitionDuration: `${(pattern[phase] || 1) * 1000}ms` }}
          />
          <div
            className={`absolute inset-4 rounded-full bg-[#234B38]/20 transition-transform ease-in-out ${scaleClass}`}
            style={{ transitionDuration: `${(pattern[phase] || 1) * 1000}ms` }}
          />
          <div className="relative z-10 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#234B38]" data-testid="breath-phase">
              {done ? "Complete" : running ? PHASE_LABELS[phase] : "Ready"}
            </p>
            <p className="text-4xl font-medium text-[#1A2E24] mt-1" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="breath-seconds">
              {done ? "✓" : secondsLeft}
            </p>
            <p className="text-[10px] text-[#5C7568] uppercase tracking-wider mt-1">
              Cycle {Math.min(cycle, cycles)} / {cycles}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-6">
          <button
            onClick={start}
            data-testid="breath-start-btn"
            className="rounded-full bg-[#234B38] hover:bg-[#1A382A] text-white px-6 py-3 font-medium flex items-center gap-2 transition-colors shadow-md no-tap-highlight"
          >
            {running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {done ? "Restart" : running ? "Pause" : "Begin"}
          </button>
          <button
            onClick={reset}
            data-testid="breath-reset-btn"
            className="rounded-full bg-transparent border border-[#E2EBE6] text-[#1A2E24] px-4 py-3 hover:bg-[#E2EBE6]/40 transition-colors no-tap-highlight"
            aria-label="Reset"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BreathworkTimer;
